import { Tabs, TabsContent, TabsList, TabsTrigger } from '@alchemer/ui/components/tabs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CreatorGuard } from '@/components/CreatorGuard/CreatorGuard';
import { DataTableType } from '@/components/DataTable/constants/dataTableConstants';
import DataTable from '@/components/DataTable/DataTable';
import { useDatasetTable, useSourceTable } from '@/components/DataTable/hooks/useDataTable';
import PageNotFound from '@/components/Error/PageNotFound/PageNotFound';
import { PAGE_IDS } from '@/constants/pages';
import { tw } from '@/lib/tailwind';

/**
 * PRIMARY ROUTE: Data Management with Tabs
 *
 * Pure Alchemer functionality for managing datasets and data sources.
 * This route does not use ThoughtSpot - it's entirely custom Alchemer UI.
 *
 * Features:
 * - Tab-based interface for datasets and sources
 * - Display available data sources in a table
 * - Data source configuration and management
 * - FAQ/Help panel for data source questions
 * - Toggle FAQ visibility
 * - Filter datasets by surveyId (e.g., ?surveyId=123456)
 *
 * URL Pattern: /dashboard/_primary-routes/data/{tab}
 * - /dashboard/_primary-routes/data/datasets
 * - /dashboard/_primary-routes/data/sources
 * - /dashboard/_primary-routes/data/datasets?surveyId=123456 (filtered view)
 *
 * NOTE: Updated to use tab-based routing for better data management organization.
 * Route supports dynamic tab parameter to switch between datasets and sources views.
 *
 * Architecture Note:
 * Located in `_primary-routes/` because it doesn't use ThoughtSpot's prerendered embed.
 * This is pure Alchemer functionality for data management.
 */

export const Route = createFileRoute('/dashboard/_primary-routes/data-source/$tab')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    surveyId?: number | string;
  } => ({
    surveyId:
      search.surveyId != null
        ? isNaN(Number(search.surveyId))
          ? String(search.surveyId)
          : Number(search.surveyId)
        : undefined,
  }),
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading } = context.auth;

    if (!isLoading && !isAuthenticated) {
      return; // AuthProvider will handle login redirect
    }
  },
});

function RouteComponent() {
  const { tab } = Route.useParams();
  const { surveyId } = Route.useSearch();
  const navigate = useNavigate();

  const handleTabChange = (newTab: string) => {
    navigate({
      to: '/dashboard/data-source/$tab',
      params: { tab: newTab },
      search: surveyId ? { surveyId } : undefined,
    });
  };

  const handleClearSurveyFilter = () => {
    navigate({
      to: '/dashboard/data-source/$tab',
      params: { tab },
      search: undefined,
    });
  };

  const getTabTriggerClasses = (tabValue: string) =>
    tw(
      // Base styles
      'cursor-pointer text-muted-foreground border-b-solid w-fit justify-start data-[state=active]:shadow-none',
      'rounded-none border-t-0 border-x-0 border-solid border-b-3 border-b-transparent bg-white pt-1 pb-2 text-sm',
      // Hover styles
      'hover:text-foreground-800 hover:border-b-chalkboard-800',
      // Active styles
      tab === tabValue && 'text-foreground-800 border-b-chalkboard-800 bg-transparent!',
    );

  return (
    <CreatorGuard fallback={<PageNotFound />}>
      <div className="flex h-[calc(100vh-110px)] flex-col bg-white">
        <div className={tw('flex flex-col gap-2 bg-white px-8 py-5')}>
          <h3 className="text-chalkboard-950 text-xl font-semibold">Manage Datasets</h3>
          <p className="text-foreground-800 text-base">
            View and manage datasets, or visit the Sources tab to create a new data set from sources.
          </p>
        </div>
        <Tabs
          defaultValue="datasets"
          className="flex min-h-0 flex-1 flex-col gap-0 bg-white"
          value={tab}
          onValueChange={handleTabChange}
        >
          <TabsList className={tw('h-auto w-fit rounded-none bg-white p-0 pl-7')}>
            <TabsTrigger
              value="datasets"
              className={getTabTriggerClasses(PAGE_IDS.Datasets)}
            >
              Datasets
            </TabsTrigger>
            <TabsTrigger
              value="sources"
              className={getTabTriggerClasses(PAGE_IDS.Sources)}
            >
              Sources
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value="datasets"
            className="border-border min-h-0 flex-1 border-t p-5 pb-10"
          >
            <DataTable
              useTableHook={useDatasetTable}
              tableType={DataTableType.Datasets}
              surveyIdFilter={surveyId}
              onClearSurveyFilter={handleClearSurveyFilter}
            />
          </TabsContent>
          <TabsContent
            value="sources"
            className={tw('border-border min-h-0 w-full flex-1 border-t p-5 pb-10')}
          >
            <DataTable
              useTableHook={useSourceTable}
              tableType={DataTableType.Sources}
            />
          </TabsContent>
        </Tabs>
      </div>
    </CreatorGuard>
  );
}
